import { useTranslation } from 'react-i18next'
import { Col, DatePicker, Empty, Row, Spin, Table, Tooltip } from 'antd'
import { useState } from 'react'
import { SelectValue } from 'antd/lib/select'
import { Dayjs } from 'dayjs'
import { InfoCircleOutlined } from '@ant-design/icons'

import { TitleAndDivider } from '@components'
import { useConfigurationProperties, useDestinations, useEquipment, useSuppliers } from '@hooks'
import { SapContract, SimulationType } from '@types'

import { StyledSelect } from '../../StyledComponents'
import {
  StyledRow,
  Container,
  StyledText,
  StyledInput,
  StyledButton,
  StyledEmptyContainer,
  StyledMandatoryField,
} from '../components'
import { useSimulation } from './hooks'
import FormatPrice from '../../FormatPrice'

interface SimulationData {
  possibleContracts: SimulationType[]
}

const Simulation = () => {
  const { t } = useTranslation()
  const { isLoading, isConfigLoadingTime, isConfigLoadingPercentaje, deleteTicketsDays } =
    useConfigurationProperties()
  const { suppliers, suppliersOptions } = useSuppliers()
  const [supplierValue, setSupplierValue] = useState<SelectValue | undefined>()
  const [percentage, setPercentage] = useState<number | undefined>()
  const [equipmentValue, setEquipmentValue] = useState<SelectValue | undefined>()
  const [destinationsValue, setDestinationsValue] = useState<SelectValue | undefined>()
  const [depotsValue, setDepotsValue] = useState<SelectValue | undefined>()
  const [dateValue, setDateValue] = useState<Dayjs | null | undefined>()
  const [possibleContracts, setPossibleContracts] = useState<SimulationData>()
  const [depotsOptions, setDepotsOptions] = useState<
    { label: string; value: number }[] | undefined
  >([])
  const { simulate, isLoading: isSimulating } = useSimulation()
  const { equipments } = useEquipment()
  const { destinationsOptions } = useDestinations()
  const columns = [
    {
      title: t('admin_router.admin_dashboard.contracts.carrier_contracts.TPL'),
      dataIndex: 'tradeName',
      key: 'tradeName',
    },
    {
      title: 'Posición',
      dataIndex: 'sapItems',
      key: 'position',
      render: (value: SapContract[]) => value[0].position,
    },
    {
      title: 'Subposición',
      dataIndex: 'sapItems',
      key: 'subposition',
      render: (value: SapContract[]) => value[0].subPosition,
    },
    {
      title: t('admin_router.admin_dashboard.settings.contract'),
      key: 'contract',
      render: (possibleContract: SimulationType) => (
        <>
          {possibleContract.sapItems.map(item => (
            <div key={item.id}>{item.contract}</div>
          ))}
        </>
      ),
    },
    {
      title: t('admin_router.filter_options.contract_filters.asnum'),
      key: 'asnum',
      render: (possibleContract: SimulationType) => (
        <>
          {possibleContract.sapItems.map(item => (
            <div key={item.id}>{item.asnumId}</div>
          ))}
        </>
      ),
    },
    {
      title: t('admin_router.admin_dashboard.settings.price'),
      key: 'price',
      render: (possibleContract: SimulationType) => (
        <>
          {possibleContract.sapItems.map(item => (
            <div key={item.id}>
              <FormatPrice price={item.price} currency={item.currency} />{' '}
            </div>
          ))}
        </>
      ),
    },
    {
      title: t('admin_router.admin_dashboard.settings.price_category'),
      dataIndex: 'priceCategory',
      key: 'priceCategory',
    },
  ]

  const handlePercentage = (value: number) => {
    setPercentage(value)
  }
  const handleEquipment = (value: any) => {
    setEquipmentValue(value)
  }
  const handleDestinations = (value: any) => {
    setDestinationsValue(value)
  }
  const handleDepots = (value: any) => {
    setDepotsValue(value)
  }
  const handleSuppliers = (value: any) => {
    setDepotsValue(undefined)
    setSupplierValue(value)
    const supplierSelected = suppliers?.find(supplier => supplier.id === value)
    const filteredDepots = supplierSelected?.depots.map(depot => ({
      label: depot.tag,
      value: depot.id,
    }))
    setDepotsOptions(filteredDepots)
  }

  const handleDate = (date: Dayjs | null | undefined) => {
    setDateValue(date)
  }

  const handleSimulate = () => {
    simulate(
      {
        depotId: depotsValue as number,
        destinationId: destinationsValue as number,
        percentage: percentage ? (percentage as number) : (deleteTicketsDays as number),
        equipmentId: equipmentValue as number,
        date: dateValue as unknown as Date,
      },
      {
        onSuccess: data => {
          setPossibleContracts(data)
        },
      },
    )
  }

  const handleClearFilters = () => {
    setSupplierValue(undefined)
    setDateValue(undefined)
    setDepotsValue(undefined)
    setDestinationsValue(undefined)
    setEquipmentValue(undefined)
    setPercentage(undefined)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  return (
    <Container>
      <TitleAndDivider title={t('admin_router.menu.simulation')} />

      <Spin spinning={isLoading || isConfigLoadingTime || isConfigLoadingPercentaje}>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              <StyledMandatoryField>* </StyledMandatoryField>
              {t('admin_router.filter_options.supplier')}:
            </StyledText>
          </Col>
          <Col xs={16} sm={16} md={8}>
            <StyledSelect
              options={suppliersOptions}
              maxTagCount="responsive"
              allowClear
              placeholder={t('globals.placeholder_select')}
              showSearch
              filterOption={handleFilterOptions}
              onChange={handleSuppliers}
              value={supplierValue}
            />
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              <StyledMandatoryField>* </StyledMandatoryField>
              {t('admin_router.admin_dashboard.settings.origin')}:
            </StyledText>
          </Col>
          <Col xs={16} sm={16} md={8}>
            <StyledSelect
              options={depotsOptions}
              maxTagCount="responsive"
              allowClear
              placeholder={t('globals.placeholder_select')}
              showSearch
              filterOption={handleFilterOptions}
              onChange={handleDepots}
              value={depotsValue}
            />
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              <StyledMandatoryField>* </StyledMandatoryField>
              {t('admin_router.admin_dashboard.settings.destination')}:
            </StyledText>
          </Col>
          <Col xs={16} sm={16} md={8}>
            <StyledSelect
              options={destinationsOptions}
              maxTagCount="responsive"
              allowClear
              placeholder={t('globals.placeholder_select')}
              showSearch
              filterOption={handleFilterOptions}
              onChange={handleDestinations}
              value={destinationsValue}
            />
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              <StyledMandatoryField>* </StyledMandatoryField>
              {t('admin_router.admin_dashboard.settings.truck_type')}:
            </StyledText>
          </Col>
          <Col xs={16} sm={16} md={8}>
            <StyledSelect
              allowClear
              placeholder={t('globals.placeholder_select')}
              options={equipments?.map(equipment => ({
                label: equipment.nameShort,
                value: equipment.id,
              }))}
              showSearch
              filterOption={handleFilterOptions}
              maxTagCount="responsive"
              onChange={handleEquipment}
              value={equipmentValue}
            />
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              <StyledMandatoryField>* </StyledMandatoryField>
              {t('admin_router.admin_dashboard.settings.simulation.date')}:
            </StyledText>
          </Col>
          <Col xs={16} sm={16} md={8}>
            <DatePicker
              format="YYYY/MM/DD"
              onChange={e => handleDate(e as Dayjs)}
              style={{ width: '100%' }}
              value={dateValue}
            />
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.ticket_days')}:{' '}
              <Tooltip
                title={t('admin_router.admin_dashboard.settings.simulation.percentage_message')}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </StyledText>
          </Col>
          <Col xs={16} sm={16} md={8}>
            <StyledInput
              width={100}
              min={0}
              max={100}
              onChange={e => handlePercentage(e as number)}
              value={percentage}
              placeholder="Ingresar porcentaje"
            />
          </Col>
        </StyledRow>
      </Spin>
      <StyledRow>
        <Col offset={8} xs={16} sm={16} md={8}>
          <Row justify="center" gutter={10}>
            <Col>
              <StyledButton
                onClick={handleSimulate}
                type="primary"
                disabled={
                  !supplierValue ||
                  !depotsValue ||
                  !destinationsValue ||
                  !equipmentValue ||
                  !dateValue
                }
              >
                {t('admin_router.admin_dashboard.settings.button_simulate')}
              </StyledButton>
            </Col>
            <Col>
              <StyledButton onClick={handleClearFilters}>
                {t('admin_router.admin_dashboard.settings.button_delete')}
              </StyledButton>
            </Col>
          </Row>
        </Col>
      </StyledRow>
      <StyledRow>
        {possibleContracts?.possibleContracts.length === 0 ? (
          <Col span={24}>
            <Spin spinning={isSimulating}>
              <StyledEmptyContainer>
                <Empty
                  description={t('admin_router.admin_dashboard.settings.simulation.empty_message')}
                />
              </StyledEmptyContainer>
            </Spin>
          </Col>
        ) : (
          <Table
            style={{ width: '100%' }}
            id="simulation-table"
            columns={columns}
            loading={isSimulating}
            dataSource={possibleContracts?.possibleContracts}
            rowKey="id"
            pagination={{
              position: ['bottomCenter'],
            }}
            scroll={{ x: true }}
          />
        )}
      </StyledRow>
    </Container>
  )
}
export { Simulation }
